// src/pages/Finance/OverduePayments/Overdue/UnpaidInvoicesTable.jsx
import React, { useMemo, useState } from "react"
import { Space, Table, Tag, Typography, Button, message, Input, Tooltip } from "antd"
import dayjs from "dayjs"
import moment from "moment"
import ContactStatusDrawer from "./ContactStatusDrawer"
import {
  CloudDownloadOutlined,
  EyeInvisibleOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons"
import InvoicePDFPreview from "../../../Accounting/Invoices/Helper/InvoicePDFPreview"
import {
  exportToExcel,
  exportToPdf,
  parseNumericValue,
} from "../../../../components/utils/TableUtils/exportUtils"
import GeneratePaymentReminderButton from "./GeneratePaymentReminderButton"

const { Text, Link } = Typography

function PaymentStatusTag({ record }) {
  const status = record.paymentStatus
  const totalSum = record.totalData?.totalSum || 0
  const paidAmount = record.paidAmount || 0

  if (status === 3) {
    return (
      <Tooltip title={`Paid: ${paidAmount.toFixed(2)} / ${totalSum.toFixed(2)} EUR`}>
        <Tag color="orange" style={{ margin: 0 }}>
          Partial
        </Tag>
      </Tooltip>
    )
  }
  if (status === 1)
    return (
      <Tag color="blue" style={{ margin: 0 }}>
        Proforma
      </Tag>
    )
  return (
    <Tag color="red" style={{ margin: 0 }}>
      Unpaid
    </Tag>
  )
}

export default function UnpaidInvoicesTable({ data = [], loading, onClientClick }) {
  const [filteredInfo, setFilteredInfo] = useState({})
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [reminderEmail, setReminderEmail] = useState("")

  const selectedInvoices = useMemo(
    () => data.filter((item) => selectedRowKeys.includes(item.id || item.docNumber)),
    [data, selectedRowKeys],
  )

  const selectionMeta = useMemo(() => {
    const clientIds = new Set()
    const manufacturerIds = new Set()
    selectedInvoices.forEach((inv) => {
      const clientId = inv.clientId || inv.client?.id
      if (clientId) clientIds.add(clientId)
      const mId =
        inv.manufacturer?.id ||
        inv.client?.manufacturer?.id ||
        inv.manufacturer?.company ||
        inv.client?.manufacturer?.company
      if (mId) manufacturerIds.add(mId)
    })
    return {
      isSingleClient: clientIds.size <= 1,
      isSingleManufacturer: manufacturerIds.size <= 1,
      isValidForReminder: clientIds.size === 1 && manufacturerIds.size === 1,
    }
  }, [selectedInvoices])

  const selectedManufacturer = useMemo(() => {
    if (!selectedInvoices.length || !selectionMeta.isSingleManufacturer) return null
    return selectedInvoices[0]?.manufacturer || selectedInvoices[0]?.client?.manufacturer || null
  }, [selectedInvoices, selectionMeta.isSingleManufacturer])

  const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }

  const money = (n = 0) =>
    Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const formatDate = (ts) => {
    if (!ts) return "—"
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000)
      return moment(date).format("DD.MM.YYYY")
    } catch {
      return "—"
    }
  }

  const getDaysLate = (record) => {
    const due = dayjs(record.payUntilFormatted, "DD.MM.YYYY")
    if (!due.isValid()) return 0
    const overdue = dayjs().startOf("day").diff(due, "day")
    return overdue > 0 ? overdue : 0
  }

  const getRemaining = (record) => {
    const total = record.totalData?.totalSum || 0
    if (record.paymentStatus === 3) return Math.max(0, total - (record.paidAmount || 0))
    if (record.paymentStatus === 1 && record.paidAmount)
      return Math.max(0, total - record.paidAmount)
    return total
  }

  const totalUnpaidWithVat = useMemo(() => {
    if (!selectedRowKeys.length) return 0
    return data
      .filter((row) => selectedRowKeys.includes(row.id || row.docNumber))
      .reduce((sum, row) => sum + getRemaining(row), 0)
  }, [data, selectedRowKeys])

  const grandTotalRemaining = useMemo(
    () => data.reduce((sum, row) => sum + getRemaining(row), 0),
    [data],
  )

  const clientFilters = useMemo(() => {
    const names = new Set(data.map((r) => r.client?.client).filter(Boolean))
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ text: name, value: name }))
  }, [data])

  const columns = useMemo(
    () => [
      {
        title: "Number",
        dataIndex: "docNumber",
        key: "invoiceNumber",
        align: "center",
        width: 90,
        render: (value, record) => (
          <Text>{`${record.docPrefix}${String(value).padStart(4, "0")}`}</Text>
        ),
        exportValue: (value, record) => `${record.docPrefix}${String(value).padStart(4, "0")}`,
      },
      {
        title: "Client",
        dataIndex: ["client", "client"],
        key: "client",
        align: "left",
        ellipsis: true,
        width: 100,
        filters: clientFilters,
        onFilter: (value, record) => record.client?.client === value,
        filterSearch: true,
        filteredValue: filteredInfo.client || null,
        sorter: (a, b) => (a.client?.client || "").localeCompare(b.client?.client || ""),
        render: (clientName, record) => {
          const name = clientName || "Unknown Client"
          if (onClientClick && record.clientId) {
            return (
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation()
                  onClientClick({ id: record.clientId, name })
                }}
                style={{ padding: 0, height: "auto", lineHeight: "inherit", textAlign: "left" }}>
                {name}
              </Button>
            )
          }
          return <Text>{name}</Text>
        },
        exportValue: (value, record) => record.client?.client || "Unknown Client",
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        width: 65,
        filters: [
          { text: "Unpaid", value: 0 },
          { text: "Proforma", value: 1 },
          { text: "Partial", value: 3 },
        ],
        onFilter: (value, record) => record.paymentStatus === value,
        render: (_, record) => <PaymentStatusTag record={record} />,
        exportValue: (_, record) => {
          if (record.paymentStatus === 3) return "Partial"
          if (record.paymentStatus === 1) return "Proforma"
          return "Unpaid"
        },
      },
      {
        title: "Created",
        dataIndex: "documentDate",
        key: "created",
        align: "center",
        width: 88,
        render: (ts) => (
          <Text italic style={{ color: "#595959" }}>
            {formatDate(ts)}
          </Text>
        ),
        exportValue: (ts) => formatDate(ts),
      },
      {
        title: "Pay Until",
        dataIndex: "payUntilFormatted",
        key: "payUntil",
        align: "center",
        width: 88,
        render: (value) => (
          <Text italic style={{ color: "#595959" }}>
            {value}
          </Text>
        ),
        exportValue: (value) => value,
      },
      {
        title: "Contact",
        dataIndex: "overdueContactStatus",
        key: "contact",
        align: "center",
        width: 120,
        render: (value, record) => (
          <ContactStatusDrawer text={value || "No Contact"} record={record} />
        ),
        exportValue: (value) => value || "No Contact",
      },
      {
        title: "",
        dataIndex: "downloadUrl",
        key: "download",
        width: 28,
        align: "center",
        render: (value) =>
          value ? (
            <Link href={value} target="_blank" rel="noopener noreferrer">
              <CloudDownloadOutlined />
            </Link>
          ) : (
            "—"
          ),
        noExport: true,
      },
      {
        title: "",
        dataIndex: "downloadUrl",
        key: "preview",
        width: 28,
        align: "center",
        // render: (val, rec) => <button onClick={() => console.log(rec)}>log</button>,
        render: (value) =>
          value ? (
            <InvoicePDFPreview pdfUrl={value} />
          ) : (
            <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
          ),
        noExport: true,
      },
      {
        title: "Late",
        key: "daysLate",
        align: "center",
        width: 50,
        render: (_, record) => {
          const days = getDaysLate(record)
          return <Text type={days > 0 ? "danger" : undefined}>{days}</Text>
        },
        exportValue: (_, record) => getDaysLate(record),
        // render: (rec, val) => <button onClick={() => console.log(val)}>log</button>,
      },
      {
        title: "Sum",
        dataIndex: ["totalData", "totalSum"],
        key: "invoiceSum",
        dataType: "numeric",
        align: "right",
        width: 80,
        render: (value) => <Text style={{ color: "#aaa" }}>{money(value)}</Text>,
        exportValue: (value) => money(parseNumericValue(value)),
      },
      {
        title: "Remaining",
        key: "unpaidSum",
        dataType: "numeric",
        align: "right",
        width: 85,
        render: (_, record) => {
          const rem = getRemaining(record)
          const isPartial =
            record.paymentStatus === 3 || (record.paymentStatus === 1 && record.paidAmount)
          return (
            <Text style={{ color: "red", fontWeight: isPartial ? 600 : 400 }}>{money(rem)}</Text>
          )
        },
        exportValue: (_, record) => money(getRemaining(record)),
      },
    ],
    [clientFilters, filteredInfo, onClientClick],
  )

  const handleTableChange = (_, filters) => setFilteredInfo(filters)

  const handleExportExcelClick = () => {
    if (!selectedRowKeys.length) {
      message.info("Please select rows to export.")
      return
    }
    const dataToExport = data.filter((item) => selectedRowKeys.includes(item.id || item.docNumber))
    const exportableColumns = columns.filter((col) => col.key !== "contact" && !col.noExport)
    exportToExcel(dataToExport, exportableColumns, `Unpaid_Invoices_${dayjs().format("YYYYMMDD")}`)
  }

  const handleExportPdfClick = () => {
    if (!selectedRowKeys.length) {
      message.info("Please select rows to export.")
      return
    }
    const dataToExport = data.filter((item) => selectedRowKeys.includes(item.id || item.docNumber))
    const exportableColumns = columns.filter((col) => col.key !== "contact" && !col.noExport)
    const selectedTotal = dataToExport.reduce((sum, row) => sum + getRemaining(row), 0)
    exportToPdf(dataToExport, exportableColumns, `Unpaid_Invoices_${dayjs().format("YYYYMMDD")}`, {
      title: "Selected Unpaid Invoices",
      totalLabel: "Total Remaining (vat inc.):",
      totalValue: selectedTotal,
      orientation: "landscape",
      columnStyles: { 1: { cellWidth: 50 }, 9: { halign: "right" } },
    })
  }

  const reminderDisableReason = useMemo(() => {
    if (!selectedInvoices.length) return "Select at least one invoice."
    if (!selectionMeta.isSingleClient) return "Select invoices from one client only."
    if (!selectionMeta.isSingleManufacturer) return "Select invoices from one manufacturer only."
    return null
  }, [selectedInvoices, selectionMeta])

  return (
    <>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Tag color="#f5f5f5" style={{ padding: "10px 15px", border: "1px solid #d9d9d9" }}>
          <Space>
            <Text italic style={{ fontSize: 18, color: "black" }}>
              {selectedRowKeys.length > 0 ? "Selected remaining:" : "Total remaining:"}
            </Text>
            <Text strong style={{ fontSize: 18, color: "#f5222d" }}>
              {money(selectedRowKeys.length > 0 ? totalUnpaidWithVat : grandTotalRemaining)} Eur
            </Text>
          </Space>
        </Tag>

        <Space>
          <Input
            placeholder="Email for reminder..."
            value={reminderEmail}
            onChange={(e) => setReminderEmail(e.target.value)}
            style={{ width: 190 }}
            allowClear
          />
          <GeneratePaymentReminderButton
            selectedInvoices={selectedInvoices}
            manufacturer={selectedManufacturer}
            email={reminderEmail}
            disabled={!!reminderDisableReason || !reminderEmail?.trim()}
            disableReason={
              reminderDisableReason || (!reminderEmail?.trim() ? "Enter an email address." : null)
            }
          />
          <Button icon={<FilePdfOutlined />} onClick={handleExportPdfClick} disabled={!data.length}>
            Export PDF
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcelClick}
            disabled={!data.length}>
            Export Excel
          </Button>
        </Space>
      </div>

      <Table
        size="small"
        rowKey={(row) => row.id || row.docNumber}
        columns={columns}
        dataSource={data}
        bordered
        rowSelection={rowSelection}
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />
    </>
  )
}
